<template>
  <div id="app">
    <Header />

    <main>
      <router-view v-slot="{ Component }">
        <transition name="lf-fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <Footer />

    <!-- Cookie Banner + Modal -->
    <CookieBanner
      @cookies-accepted="handleCookiesAccepted"
      @open-settings="openCookieSettings"
    />
    <CookieSettingsModal
      v-if="showCookieModal"
      :preferences="cookiePreferences"
      @close="closeCookieModal"
      @save="saveCookiePreferences"
    />

    <!-- Botón volver arriba -->
    <Transition name="lf-fade-up">
      <button
        v-if="showBackToTop"
        class="lf-back-to-top"
        aria-label="Volver arriba"
        @click="scrollToTop"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="19" x2="12" y2="5"/>
          <polyline points="5 12 12 5 19 12"/>
        </svg>
      </button>
    </Transition>

    <!-- WhatsApp flotante -->
    <a
      href="https://wa.me/56950111647?text=Hola%20LaFuerza%20IT%2C%20quiero%20cotizar%20un%20proyecto"
      target="_blank"
      rel="noopener noreferrer"
      class="lf-wa-float"
      aria-label="Contactar por WhatsApp"
      @click="trackWhatsAppClick('main')"
    >
      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
      <span class="lf-wa-float__tooltip">
        <strong>Hablemos</strong>
        <small>Respuesta en minutos</small>
      </span>
    </a>
  </div>
</template>

<script>
import Header from './components/Header.vue'
import Footer from './components/Footer.vue'
import CookieBanner from './components/CookieBanner.vue'
import CookieSettingsModal from './components/CookieSettingsModal.vue'

export default {
  name: 'App',
  components: { Header, Footer, CookieBanner, CookieSettingsModal },
  data() {
    return {
      showBackToTop: false,
      showCookieModal: false,
      cookiePreferences: {
        essential: true,
        analytics: false,
        functional: false,
        marketing: false
      }
    }
  },
  watch: {
    $route(to) {
      window.scrollTo({ top: 0, behavior: 'instant' })
      this.trackPageView(to.path)
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    this.loadCookiePreferences()
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.showBackToTop = window.scrollY > 400
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      this.track('scroll_to_top', { label: 'back_to_top' })
    },
    loadCookiePreferences() {
      const saved = localStorage.getItem('cookiePreferences')
      if (!saved) return
      try {
        this.cookiePreferences = JSON.parse(saved)
      } catch (e) {
        console.warn('Preferencias de cookies inválidas, se restablecen.')
        localStorage.removeItem('cookiePreferences')
      }
    },
    openCookieSettings() {
      this.showCookieModal = true
      document.body.style.overflow = 'hidden'
    },
    closeCookieModal() {
      this.showCookieModal = false
      document.body.style.overflow = ''
    },
    saveCookiePreferences(preferences) {
      this.cookiePreferences = preferences
      localStorage.setItem('cookiePreferences', JSON.stringify(preferences))
      localStorage.setItem('cookieConsentDate', new Date().toISOString())

      const consentType = (preferences.analytics || preferences.functional || preferences.marketing)
        ? 'all'
        : 'essential'
      localStorage.setItem('cookieConsent', consentType)

      this.applyCookieServices(preferences)
      this.closeCookieModal()
    },
    handleCookiesAccepted(type) {
      const preferences = type === 'all'
        ? { essential: true, analytics: true, functional: true, marketing: true }
        : { essential: true, analytics: false, functional: false, marketing: false }

      this.saveCookiePreferences(preferences)
    },
    applyCookieServices(preferences) {
      // Google Consent Mode v2 (GTM ya está cargado en index.html)
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: preferences.analytics ? 'granted' : 'denied',
          ad_storage: preferences.marketing ? 'granted' : 'denied',
          ad_user_data: preferences.marketing ? 'granted' : 'denied',
          ad_personalization: preferences.marketing ? 'granted' : 'denied'
        })
      }

      // Meta Pixel
      if (typeof window.fbq === 'function') {
        window.fbq('consent', preferences.marketing ? 'grant' : 'revoke')
      }
    },
    track(eventName, params = {}) {
      if (!this.cookiePreferences.analytics) return
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, {
          event_category: 'engagement',
          ...params
        })
      }
    },
    trackPageView(path) {
      this.track('page_view', { page_path: path, page_title: document.title })
    },
    trackWhatsAppClick(type) {
      this.track('whatsapp_click', { event_label: `whatsapp_${type}` })
    }
  }
}
</script>

<style>
/* =========================================================
   Transiciones de ruta
   ========================================================= */
.lf-fade-enter-active,
.lf-fade-leave-active {
  transition: opacity 0.2s ease;
}
.lf-fade-enter-from,
.lf-fade-leave-to {
  opacity: 0;
}

.lf-fade-up-enter-active,
.lf-fade-up-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.lf-fade-up-enter-from,
.lf-fade-up-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

/* =========================================================
   Botón volver arriba
   ========================================================= */
.lf-back-to-top {
  position: fixed;
  bottom: 100px;
  right: 28px;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: var(--lf-surface);
  border: 1px solid var(--lf-border-2);
  color: var(--lf-text);
  cursor: pointer;
  z-index: 998;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.25s, background 0.25s, transform 0.25s;
}
.lf-back-to-top:hover {
  border-color: var(--lf-accent);
  color: var(--lf-accent);
  transform: translateY(-2px);
}

/* =========================================================
   WhatsApp flotante
   ========================================================= */
.lf-wa-float {
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #25D366;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.lf-wa-float:hover {
  transform: translateY(-2px) scale(1.04);
  box-shadow: 0 12px 32px rgba(37, 211, 102, 0.5);
}

.lf-wa-float__tooltip {
  position: absolute;
  right: 70px;
  top: 50%;
  transform: translateY(-50%) translateX(8px);
  background: var(--lf-surface);
  border: 1px solid var(--lf-border-2);
  color: var(--lf-text);
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.3;
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.2s, transform 0.2s, visibility 0.2s;
}
.lf-wa-float__tooltip strong {
  font-weight: 600;
  font-size: 13.5px;
}
.lf-wa-float__tooltip small {
  color: var(--lf-text-mute);
  font-size: 11.5px;
}
.lf-wa-float:hover .lf-wa-float__tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateY(-50%) translateX(0);
}

/* =========================================================
   Responsive
   ========================================================= */
@media (max-width: 768px) {
  .lf-wa-float { width: 52px; height: 52px; bottom: 20px; right: 20px; }
  .lf-wa-float__tooltip { display: none; }
  .lf-back-to-top { width: 42px; height: 42px; bottom: 84px; right: 22px; }
}

/* =========================================================
   Reduced motion
   ========================================================= */
@media (prefers-reduced-motion: reduce) {
  .lf-fade-enter-active,
  .lf-fade-leave-active,
  .lf-fade-up-enter-active,
  .lf-fade-up-leave-active {
    transition: none;
  }
  .lf-wa-float,
  .lf-back-to-top {
    transition: none;
  }
}

/* =========================================================
   Print
   ========================================================= */
@media print {
  .lf-wa-float,
  .lf-back-to-top {
    display: none !important;
  }
}
</style>